import { signedFetch } from '../../super-picker/services/api';

const SHARE_OVERRIDE_KEY = 'appDownloadShare';
const UPLOAD_OVERRIDE_KEY = 'appDownloadUpload';

const OVERRIDE_PARAMS: Record<string, string> = {
  [SHARE_OVERRIDE_KEY]: 'appdownloadshare',
  [UPLOAD_OVERRIDE_KEY]: 'appdownloadupload',
};

const latchOverridesFromUrl = (): void => {
  try {
    const params = new URLSearchParams(window.location.search);
    Object.entries(OVERRIDE_PARAMS).forEach(([key, param]) => {
      const raw = params.get(param);
      if (raw === '1' || raw === 'true') window.localStorage.setItem(key, '1');
      else if (raw === '0' || raw === 'false') window.localStorage.removeItem(key);
    });
  } catch {
    /* no location / storage */
  }
};

if (typeof window !== 'undefined') latchOverridesFromUrl();

const readOverride = (key: string): boolean | null => {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === '1' || raw === 'true') return true;
    if (raw === '0' || raw === 'false') return false;
  } catch {
    /* storage blocked */
  }
  return null;
};

const trace = (message: string): void => {
  // eslint-disable-next-line no-console
  console.info(`[file-download] ${message}`);
};

const isWebKitWebView = (): boolean => {
  const ua = navigator.userAgent;
  if (!/AppleWebKit/.test(ua)) return false;
  return !/(Safari|Chrome|CriOS|FxiOS|EdgiOS|Edg)\//.test(ua);
};

const isAndroidWebView = (): boolean => {
  const ua = navigator.userAgent;
  if (!/Android/.test(ua)) return false;
  if (/;\s*wv[;)]/.test(ua) || /\bVersion\/[\d.]+\b.*\bChrome\//.test(ua)) return true;
  return !/(Chrome|CriOS|Firefox|FxiOS|SamsungBrowser|OPR|OPT|EdgA?|UCBrowser|MiuiBrowser|HuaweiBrowser)\//.test(ua);
};

const hasNativeBridge = (): boolean => {
  const w = window as unknown as { ReactNativeWebView?: unknown; webkit?: { messageHandlers?: unknown } };
  return !!(w.ReactNativeWebView || w.webkit?.messageHandlers);
};

export const isInWebView = (): boolean =>
  isAndroidWebView() || isWebKitWebView() || hasNativeBridge();

const shouldTryShare = (): boolean => {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
  return readOverride(SHARE_OVERRIDE_KEY) ?? isWebKitWebView();
};

const shouldTryUpload = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return readOverride(UPLOAD_OVERRIDE_KEY) ?? isInWebView();
};

const UPLOAD_MAX_BYTES = 6 * 1024 * 1024;

const shareBlobFile = async (blob: Blob, filename: string, mimeType: string): Promise<boolean> => {
  try {
    const file = new File([blob], filename, { type: mimeType || blob.type || 'application/octet-stream' });
    if (typeof navigator.canShare === 'function' && !navigator.canShare({ files: [file] })) {
      return false;
    }
    await navigator.share({ files: [file], title: filename });
    return true;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return true;
    trace(`share failed: ${err instanceof Error ? err.message : String(err)}`);
    return false;
  }
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      if (comma < 0) reject(new Error('Failed to encode blob'));
      else resolve(result.slice(comma + 1));
    };
    reader.readAsDataURL(blob);
  });

const navigateToDownload = (url: string, filename: string): void => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  if (!isInWebView()) a.target = '_blank';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
  a.remove();
};

const downloadViaStorage = async (
  blob: Blob,
  filename: string,
  mimeType: string,
): Promise<boolean> => {
  if (blob.size > UPLOAD_MAX_BYTES) return false;
  try {
    const dataBase64 = await blobToBase64(blob);
    const response = await signedFetch(
      '/api/app-downloads/blob',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename, dataBase64, contentType: mimeType || undefined }),
      },
      true,
      false,
    );
    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      trace(`upload rejected: HTTP ${response.status} ${detail.slice(0, 200)}`);
      return false;
    }
    const payload = (await response.json()) as { url?: unknown };
    if (typeof payload.url !== 'string' || !payload.url) {
      trace('upload returned no url');
      return false;
    }
    navigateToDownload(payload.url, filename);
    return true;
  } catch (err) {
    trace(`upload failed: ${err instanceof Error ? err.message : String(err)}`);
    return false;
  }
};

const downloadBlob = (blob: Blob, filename: string, mimeType: string): void => {
  const typed =
    mimeType && (!blob.type || blob.type === 'application/octet-stream')
      ? new Blob([blob], { type: mimeType })
      : blob;
  const url = URL.createObjectURL(typed);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/**
 * Delivers a fetched blob under `filename`, using Web Share or a short-lived
 * storage URL inside native WebViews (where blob: URLs lose their names).
 */
export async function saveBlobDownload(
  blob: Blob,
  filename: string,
  mimeType?: string | null,
): Promise<void> {
  const mime = mimeType || blob.type || 'application/octet-stream';
  trace(`ua=${navigator.userAgent} | share=${shouldTryShare()} upload=${shouldTryUpload()} | ${Math.round(blob.size / 1024)} KB`);

  if (shouldTryShare()) {
    const shared = await shareBlobFile(blob, filename, mime);
    if (shared) {
      trace('delivered via share sheet');
      return;
    }
  }

  if (shouldTryUpload()) {
    const downloaded = await downloadViaStorage(blob, filename, mime);
    if (downloaded) {
      trace('delivered via signed download URL');
      return;
    }
  }

  trace('delivered via blob download');
  downloadBlob(blob, filename, mime);
}
